import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ASSIGNABLE_ROLES, useAssignableRoles, type AssignableRole } from '@/lib/can-manage-role';
import { grantErrorMessage, useCustomRoles } from '@/lib/custom-roles';
import { ROLE_LABELS } from '@/lib/role-display';
import { memberName, useChangeRole, type Member } from '@/lib/users';

interface Props { member: Member | null; onOpenChange: (open: boolean) => void }

// Valeur du select pour un custom role (les rôles standards gardent leur code)
const CUSTOM = 'custom:';

export function ChangeRoleDialog({ member, onOpenChange }: Props) {
  const roles = useAssignableRoles();
  const { data: customRoles } = useCustomRoles(!!member);
  const changeRole = useChangeRole();
  const [choice, setChoice] = useState('');
  const [rank, setRank] = useState<AssignableRole | ''>('');

  const resetMutation = changeRole.reset;
  useEffect(() => {
    if (!member) return;
    setChoice('');
    setRank(ASSIGNABLE_ROLES.includes(member.role as AssignableRole) ? member.role as AssignableRole : '');
    resetMutation();
  }, [member, resetMutation]);

  const currentCustom = customRoles?.data.find((r) => r.id === member?.customRoleId);
  const customId = choice.startsWith(CUSTOM) ? choice.slice(CUSTOM.length) : null;
  const selectedCustom = customRoles?.data.find((r) => r.id === customId);

  const confirm = () => {
    if (!member || !choice) return;
    // Rôle standard : quitte le custom role éventuel. Custom role : le rang ne bouge que s'il change.
    const body: { role?: AssignableRole; customRoleId?: string | null } = {};
    if (customId) {
      body.customRoleId = customId;
      if (rank && rank !== member.role) body.role = rank;
    } else {
      if (choice !== member.role) body.role = choice as AssignableRole;
      if (member.customRoleId) body.customRoleId = null;
    }
    const label = selectedCustom?.name ?? ROLE_LABELS[choice as AssignableRole];
    // Le refus (dernier OWNER : 409, propre rôle / canGrant : 403) reste affiché dans la modale
    changeRole.mutate({ membershipId: member.membershipId, ...body }, {
      onSuccess: () => {
        toast.success(`${memberName(member)} est maintenant ${label}`);
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={!!member} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Changer le rôle</DialogTitle>
          {member && (
            <DialogDescription>
              {memberName(member)} — rôle actuel :{' '}
              {currentCustom ? `${currentCustom.name} (rang ${ROLE_LABELS[member.role]})` : ROLE_LABELS[member.role]}
            </DialogDescription>
          )}
        </DialogHeader>
        <div>
          <Label htmlFor="change-role">Nouveau rôle</Label>
          <Select value={choice} onValueChange={setChoice}>
            <SelectTrigger id="change-role" aria-label="Nouveau rôle"><SelectValue placeholder="Choisir un rôle" /></SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Rôles standards</SelectLabel>
                {roles.filter((r) => r !== member?.role || !!member.customRoleId)
                  .map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectGroup>
              {!!customRoles?.data.length && (
                <SelectGroup>
                  <SelectLabel>Rôles personnalisés</SelectLabel>
                  {customRoles.data.filter((r) => r.id !== member?.customRoleId)
                    .map((r) => <SelectItem key={r.id} value={`${CUSTOM}${r.id}`}>{r.name}</SelectItem>)}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        </div>
        {customId && (
          <div>
            <Label htmlFor="change-rank">Rang</Label>
            <Select value={rank} onValueChange={(v) => setRank(v as AssignableRole)}>
              <SelectTrigger id="change-rank" aria-label="Rang"><SelectValue placeholder="Choisir un rang" /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
              </SelectContent>
            </Select>
            {rank && (
              <p className="text-xs text-muted-foreground mt-1">
                Basé sur {ROLE_LABELS[rank]} : le rang décide qui peut gérer ce membre, le rôle personnalisé ses permissions.
              </p>
            )}
          </div>
        )}
        {changeRole.isError && (
          <p role="alert" className="text-sm text-danger">{grantErrorMessage(changeRole.error, 'Erreur lors du changement de rôle')}</p>
        )}
        <DialogFooter>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={confirm} disabled={!choice || (!!customId && !rank) || changeRole.isPending}>
            {changeRole.isPending ? 'Enregistrement…' : 'Confirmer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
